import sharesproApi from 'rtkQuery/sharespro/sharesproApiService';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import mapNotificationFromApi from 'rtkQuery/sharespro/mapper/modules/notifications/mapNotificationsFromApi';
import mapPaginationListFromApi from 'rtkQuery/sharespro/mapper/shared/mapPaginationListFromApi';

interface ApiNotificationListQueryParams extends ApiPaginationQueryParams {}

interface ApiNotificationViewQueryParams {
  id: string;
}

interface ApiNotificationActionParams {
  id: string;
}

interface ApiNotificationBulkSeenParams {
  ids: string;
}

export const notificationsApi = sharesproApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchNotificationCount: builder.query<
      SharesproApiResponse<ApiNotificationCountResponseData>,
      ApiNotificationCountQueryParams | void
    >({
      query: (params) => ({
        url: '/v3/client/notification/count',
        method: 'GET',
        sendToken: true,
        params,
      }),
    }),
    fetchNotificationList: builder.query<
      PaginationList<AppNotification>,
      ApiNotificationListQueryParams
    >({
      query: (params) => ({
        url: '/v3/client/notification/list',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiPaginationListResponse<ApiNotificationResponse>>
      ) {
        return mapPaginationListFromApi<AppNotification, ApiNotificationResponse>(
          baseQueryReturnValue.data,
          mapNotificationFromApi
        );
      },
      providesTags: ['Notifications'],
    }),

    fetchNotification: builder.query<
      AppNotification,
      ApiNotificationViewQueryParams
    >({
      query: (params) => ({
        url: '/v3/client/notification/view',
        method: 'GET',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<ApiNotificationResponse>
      ) {
        return mapNotificationFromApi(baseQueryReturnValue.data);
      },
      providesTags: (result, error, { id }) => [{ type: 'Notifications', id }],
    }),

    markNotificationAsSeen: builder.mutation<
      boolean,
      ApiNotificationActionParams
    >({
      query: (params) => ({
        url: '/v3/client/notification/seen',
        method: 'POST',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<boolean>
      ) {
        return baseQueryReturnValue.data ?? false;
      },
      invalidatesTags: (result, error, { id }) => [
        'Notifications',
        { type: 'Notifications', id },
      ],
    }),

    markNotificationsAsBulkSeen: builder.mutation<
      boolean,
      ApiNotificationBulkSeenParams
    >({
      query: (params) => ({
        url: '/v3/client/notification/seen',
        method: 'POST',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<boolean>
      ) {
        return baseQueryReturnValue.data ?? false;
      },
      invalidatesTags: ['Notifications'],
    }),

    markNotificationAsRead: builder.mutation<
      boolean,
      ApiNotificationActionParams
    >({
      query: (params) => ({
        url: '/v3/client/notification/read',
        method: 'POST',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<boolean>
      ) {
        return baseQueryReturnValue.data ?? false;
      },
      invalidatesTags: (result, error, { id }) => [
        'Notifications',
        { type: 'Notifications', id },
      ],
    }),

    requestNotification: builder.mutation<
      boolean,
      ApiNotificationActionParams
    >({
      query: (params) => ({
        url: '/v3/client/notification/request',
        method: 'POST',
        sendToken: true,
        params,
      }),
      transformResponse(
        baseQueryReturnValue: SharesproApiResponse<boolean>
      ) {
        return baseQueryReturnValue.data ?? false;
      },
      invalidatesTags: (result, error, { id }) => [
        'Notifications',
        { type: 'Notifications', id },
      ],
    }),
  }),
});

export const {
  useFetchNotificationCountQuery,
  useFetchNotificationListQuery,
  useFetchNotificationQuery,
  useMarkNotificationAsSeenMutation,
  useMarkNotificationAsReadMutation,
  useMarkNotificationsAsBulkSeenMutation,
  useRequestNotificationMutation,
} = notificationsApi;
